import React, { useEffect, useState } from 'react';
import { Search, Download, RefreshCw, X, UserPlus, ChevronUp, ChevronDown, ChevronRight } from 'lucide-react';
import { authConfig } from '../../config/auth';
import { useAuth } from '../../contexts/AuthContext';
import { exportToCsv } from '../../utils/exportCsv';
import ConfirmActionModal from '../../components/ConfirmActionModal';
import type { BackOfficeCompanySummary, BackOfficeCompanyDetail, BackOfficeMomentRow, BackOfficeCompanyRewardRow, BackOfficeUserRow } from '@crewcast/shared';

/** A destructive action awaiting the second click in ConfirmActionModal. */
type PendingAction =
  | { kind: 'archiveUser' | 'deleteUser'; user: BackOfficeUserRow }
  | { kind: 'cancelCompany' | 'reactivateCompany' };

/** Users who can no longer sign in. They get no further actions in the back office. */
function isRemovedUser(u: BackOfficeUserRow): boolean {
  return u.status === 'archived' || u.status === 'deleted';
}

function userLabel(u: BackOfficeUserRow): string {
  return [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email;
}

/** Editable account/billing fields in the company-detail modal. */
type AccountForm = {
  subscriptionTier: string;
  subscriptionStatus: string;
  industry: string;
  contactEmail: string;
  billingEmail: string;
  contractStartDate: string;
  contractEndDate: string;
  billingCycle: string;
};

const EMPTY_ACCOUNT_FORM: AccountForm = {
  subscriptionTier: '',
  subscriptionStatus: '',
  industry: '',
  contactEmail: '',
  billingEmail: '',
  contractStartDate: '',
  contractEndDate: '',
  billingCycle: '',
};

/**
 * Derive the next billing date from the contract start date, stepped forward by
 * the billing cycle until it lands on/after today. Returns null if either input
 * is missing/invalid. Display-only — nothing is stored.
 */
function computeNextBillingDate(contractStartDate: string, billingCycle: string): Date | null {
  if (!contractStartDate || !billingCycle) return null;
  const start = new Date(`${contractStartDate}T00:00:00`);
  if (isNaN(start.getTime())) return null;
  const monthsPerCycle =
    billingCycle === 'monthly' ? 1 : billingCycle === 'bimonthly' ? 2 : billingCycle === 'yearly' ? 12 : 0;
  if (monthsPerCycle === 0) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const next = new Date(start);
  let guard = 0;
  while (next < today && guard < 1200) {
    next.setMonth(next.getMonth() + monthsPerCycle);
    guard++;
  }
  return next;
}

/** Collapsible section used inside the company-detail modal. */
function CollapsibleSection({
  title,
  open,
  onToggle,
  children,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="bo-modal-section">
      <button
        type="button"
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, width: '100%',
          background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left',
        }}
      >
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <span className="bo-modal-section-title" style={{ margin: 0 }}>{title}</span>
      </button>
      {open && <div style={{ marginTop: 12 }}>{children}</div>}
    </div>
  );
}

export default function BackOfficeAccountsPage() {
  const { token, user: me } = useAuth();
  const [companies, setCompanies] = useState<BackOfficeCompanySummary[]>([]);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'canceled'>('all');

  // Two-click destructive actions (archive/delete user, cancel/reactivate company)
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionNotice, setActionNotice] = useState('');
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const [detail, setDetail] = useState<BackOfficeCompanyDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  // Saving a company-settings toggle from the detail modal
  const [savingSetting, setSavingSetting] = useState<string | null>(null);
  const [settingsError, setSettingsError] = useState('');

  // Editable account/billing form in the detail modal
  const [accountForm, setAccountForm] = useState<AccountForm>(EMPTY_ACCOUNT_FORM);
  const [savingAccount, setSavingAccount] = useState(false);
  const [accountError, setAccountError] = useState('');
  const [accountSuccess, setAccountSuccess] = useState('');

  // Collapsible modal sections (all start collapsed)
  const [usersOpen, setUsersOpen] = useState(false);
  const [momentsOpen, setMomentsOpen] = useState(false);
  const [rewardsOpen, setRewardsOpen] = useState(false);

  // Moments are lazy-loaded the first time the section is expanded
  const [moments, setMoments] = useState<BackOfficeMomentRow[]>([]);
  const [momentsLoading, setMomentsLoading] = useState(false);
  const [momentsError, setMomentsError] = useState('');
  const [momentsLoadedFor, setMomentsLoadedFor] = useState<string | null>(null);

  // Rewards are lazy-loaded the first time the section is expanded
  const [rewards, setRewards] = useState<BackOfficeCompanyRewardRow[]>([]);
  const [rewardsLoading, setRewardsLoading] = useState(false);
  const [rewardsError, setRewardsError] = useState('');
  const [rewardsLoadedFor, setRewardsLoadedFor] = useState<string | null>(null);

  // Create admin form
  const NEW_COMPANY = '__new__';
  const [createOpen, setCreateOpen] = useState(false);
  const [caEmail, setCaEmail] = useState('');
  const [caFirstName, setCaFirstName] = useState('');
  const [caLastName, setCaLastName] = useState('');
  const [caCompany, setCaCompany] = useState(''); // companyId, or NEW_COMPANY
  const [caNewCompanyName, setCaNewCompanyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');

  useEffect(() => {
    fetchData();
  }, [search]); // eslint-disable-line

  async function fetchData() {
    setLoading(true);
    setError('');
    try {
      if (!token) throw new Error('Not authenticated');

      const params = new URLSearchParams({ pageSize: '200' });
      if (search) params.set('search', search);

      const res = await fetch(`${authConfig.apiUrl}/api/backoffice/companies?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to load accounts');

      setCompanies(data.companies || []);
      setTotal(data.total || 0);
    } catch (err: any) {
      setError(err.message || 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  }

  const [detailCompanyId, setDetailCompanyId] = useState<string | null>(null);

  async function openDetail(companyId: string) {
    setDetail(null);
    setDetailError('');
    setSettingsError('');
    setAccountError('');
    setAccountSuccess('');
    setAccountForm(EMPTY_ACCOUNT_FORM);
    setDetailLoading(true);
    setDetailCompanyId(companyId);
    // Reset collapsible sections + lazy-loaded data for each newly opened company
    setUsersOpen(false);
    setMomentsOpen(false);
    setMoments([]);
    setMomentsError('');
    setMomentsLoadedFor(null);
    setRewardsOpen(false);
    setRewards([]);
    setRewardsError('');
    setRewardsLoadedFor(null);
    setActionNotice('');
    try {
      await fetchDetail(companyId);
    } catch (err: any) {
      setDetailError(err.message || 'Failed to load company detail');
    } finally {
      setDetailLoading(false);
    }
  }

  // Load (or reload) the company detail + user list and sync the account form.
  async function fetchDetail(companyId: string) {
    if (!token) throw new Error('Not authenticated');

    const res = await fetch(
      `${authConfig.apiUrl}/api/backoffice/companies/${companyId}/users`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.message || 'Failed to load company detail');
    setDetail(data);
    const c = data.company || {};
    setAccountForm({
      subscriptionTier: c.subscriptionTier || '',
      subscriptionStatus: c.subscriptionStatus || '',
      industry: c.industry || '',
      contactEmail: c.contactEmail || '',
      billingEmail: c.billingEmail || '',
      contractStartDate: c.contractStartDate || '',
      contractEndDate: c.contractEndDate || '',
      billingCycle: c.billingCycle || '',
    });
  }

  function closeDetail() {
    setDetail(null);
    setDetailError('');
    setSettingsError('');
    setAccountError('');
    setAccountSuccess('');
    setActionNotice('');
    setDetailCompanyId(null);
  }

  function openAction(action: PendingAction) {
    setActionError('');
    setActionNotice('');
    setPendingAction(action);
  }

  function closeAction() {
    if (actionLoading) return;
    setPendingAction(null);
    setActionError('');
  }

  // Second click: runs the confirmed action, then refreshes the open company.
  async function runPendingAction() {
    if (!pendingAction || !detail || actionLoading) return;
    const companyId = detail.company.companyId;
    const isUserAction = pendingAction.kind === 'archiveUser' || pendingAction.kind === 'deleteUser';

    let url: string;
    if (pendingAction.kind === 'archiveUser' || pendingAction.kind === 'deleteUser') {
      const verb = pendingAction.kind === 'archiveUser' ? 'archive' : 'delete';
      url = `${authConfig.apiUrl}/api/backoffice/users/${pendingAction.user.userId}/${verb}`;
    } else {
      const verb = pendingAction.kind === 'cancelCompany' ? 'cancel' : 'reactivate';
      url = `${authConfig.apiUrl}/api/backoffice/companies/${companyId}/${verb}`;
    }

    setActionLoading(true);
    setActionError('');
    try {
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Action failed');

      let notice: string;
      if (pendingAction.kind === 'archiveUser' || pendingAction.kind === 'deleteUser') {
        const name = userLabel(pendingAction.user);
        notice =
          data.action === 'deleted' && pendingAction.kind === 'archiveUser'
            ? `${name}'s pending invite was removed.`
            : pendingAction.kind === 'archiveUser'
              ? `${name} was archived.`
              : `${name} was deleted.`;
        if (data.warning) notice += ` ${data.warning}`;
      } else {
        notice =
          pendingAction.kind === 'cancelCompany'
            ? `${detail.company.displayName} was canceled. Its users no longer have access.`
            : `${detail.company.displayName} was reactivated.`;
      }

      setPendingAction(null);
      setActionNotice(notice);
      await fetchDetail(companyId).catch(() => {});
      if (!isUserAction) await fetchData();
    } catch (err: any) {
      setActionError(err.message || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  }

  function setAccountField(key: keyof AccountForm, value: string) {
    setAccountForm((prev) => ({ ...prev, [key]: value }));
    setAccountSuccess('');
  }

  // Save the editable account/billing fields for the open company.
  async function saveAccount() {
    if (!detailCompanyId) return;
    setSavingAccount(true);
    setAccountError('');
    setAccountSuccess('');
    try {
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(
        `${authConfig.apiUrl}/api/backoffice/companies/${detailCompanyId}`,
        {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(accountForm),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to save company');
      // Sync the displayed detail with what we just saved
      setDetail((prev) =>
        prev ? { ...prev, company: { ...prev.company, ...accountForm } } : prev
      );
      setAccountSuccess('Saved');
    } catch (err: any) {
      setAccountError(err.message || 'Failed to save company');
    } finally {
      setSavingAccount(false);
    }
  }

  // Toggle a single boolean company setting. Sends the full merged settings
  // object (the backend replaces the stored value) and updates local state.
  async function toggleCompanySetting(
    key: 'suggestedTrendsEnabled' | 'pointsEnabled',
    value: boolean
  ) {
    if (!detail) return;
    const companyId = detail.company.companyId;
    const nextSettings = { ...(detail.company.settings || {}), [key]: value };
    setSavingSetting(key);
    setSettingsError('');
    try {
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(
        `${authConfig.apiUrl}/api/backoffice/companies/${companyId}/settings`,
        {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ settings: nextSettings }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to update settings');
      setDetail((prev) =>
        prev ? { ...prev, company: { ...prev.company, settings: data.settings || nextSettings } } : prev
      );
    } catch (err: any) {
      setSettingsError(err.message || 'Failed to update settings');
    } finally {
      setSavingSetting(null);
    }
  }

  async function loadMoments(companyId: string) {
    setMomentsLoading(true);
    setMomentsError('');
    try {
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(
        `${authConfig.apiUrl}/api/backoffice/companies/${companyId}/moments`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to load moments');
      setMoments(data.moments || []);
      setMomentsLoadedFor(companyId);
    } catch (err: any) {
      setMomentsError(err.message || 'Failed to load moments');
    } finally {
      setMomentsLoading(false);
    }
  }

  function toggleMoments() {
    const next = !momentsOpen;
    setMomentsOpen(next);
    // Lazy-load on first expand for this company
    if (next && detailCompanyId && momentsLoadedFor !== detailCompanyId && !momentsLoading) {
      loadMoments(detailCompanyId);
    }
  }

  async function loadRewards(companyId: string) {
    setRewardsLoading(true);
    setRewardsError('');
    try {
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(
        `${authConfig.apiUrl}/api/backoffice/companies/${companyId}/rewards`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to load rewards');
      setRewards(data.rewards || []);
      setRewardsLoadedFor(companyId);
    } catch (err: any) {
      setRewardsError(err.message || 'Failed to load rewards');
    } finally {
      setRewardsLoading(false);
    }
  }

  function toggleRewards() {
    const next = !rewardsOpen;
    setRewardsOpen(next);
    // Lazy-load on first expand for this company
    if (next && detailCompanyId && rewardsLoadedFor !== detailCompanyId && !rewardsLoading) {
      loadRewards(detailCompanyId);
    }
  }

  function resetCreateForm() {
    setCaEmail('');
    setCaFirstName('');
    setCaLastName('');
    setCaCompany('');
    setCaNewCompanyName('');
    setCreateError('');
  }

  async function handleCreateAdmin(e: React.FormEvent) {
    e.preventDefault();
    setCreateError('');
    setCreateSuccess('');

    const email = caEmail.trim();
    if (!email) {
      setCreateError('Email is required.');
      return;
    }
    if (!caCompany) {
      setCreateError('Select a company, or choose "New company…".');
      return;
    }
    if (caCompany === NEW_COMPANY && !caNewCompanyName.trim()) {
      setCreateError('Enter a name for the new company.');
      return;
    }

    setCreating(true);
    try {
      if (!token) throw new Error('Not authenticated');

      const payload: Record<string, string> = {
        email,
        firstName: caFirstName.trim(),
        lastName: caLastName.trim(),
      };
      if (caCompany === NEW_COMPANY) {
        payload.companyName = caNewCompanyName.trim();
      } else {
        payload.companyId = caCompany;
      }

      const res = await fetch(`${authConfig.apiUrl}/api/backoffice/admins`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to create admin');

      const companyLabel = data.company?.displayName || 'their company';
      setCreateSuccess(
        `Invitation sent to ${email} as an admin of ${companyLabel}${data.company?.created ? ' (new company created)' : ''}.`
      );
      resetCreateForm();
      setCreateOpen(false);
      await fetchData();
    } catch (err: any) {
      setCreateError(err.message || 'Failed to create admin');
    } finally {
      setCreating(false);
    }
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setSearch(searchInput.trim());
  }

  function handleExport() {
    exportToCsv(
      `crewcast-accounts-${new Date().toISOString().slice(0, 10)}`,
      [
        'companyId', 'displayName', 'slug', 'subscriptionTier', 'subscriptionStatus',
        'createdAt', 'adminCount', 'employeeCount', 'totalUsers',
        'invitesSent', 'loggedInCount', 'pendingCount', 'lastActivityAt',
      ],
      companies as unknown as Record<string, unknown>[]
    );
  }

  const visibleCompanies = companies.filter((c) =>
    statusFilter === 'all'
      ? true
      : statusFilter === 'canceled'
        ? c.subscriptionStatus === 'canceled'
        : c.subscriptionStatus !== 'canceled'
  );

  const isCanceled = detail?.company.subscriptionStatus === 'canceled';
  const liveAdminCount = detail
    ? detail.users.filter((u) => u.role === 'admin' && !isRemovedUser(u)).length
    : 0;

  const totalAdmins = companies.reduce((s, c) => s + c.adminCount, 0);
  const totalEmployees = companies.reduce((s, c) => s + c.employeeCount, 0);
  const totalPending = companies.reduce((s, c) => s + c.pendingCount, 0);

  return (
    <>
      <div className="bo-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div>
          <h1 className="bo-page-title">Accounts & Users</h1>
          <p className="bo-page-subtitle">Company roster with user funnel stats — click a row to see details</p>
        </div>
        <button
          className="bo-btn bo-btn-primary"
          onClick={() => { setCreateOpen((v) => !v); setCreateSuccess(''); }}
        >
          {createOpen ? <ChevronUp size={14} /> : <UserPlus size={14} />}
          {createOpen ? 'Cancel' : 'Create Admin'}
        </button>
      </div>

      {createSuccess && (
        <div className="bo-summary-row" style={{ marginBottom: 16 }}>
          <div className="bo-summary-item" style={{ flex: 1, borderColor: 'var(--color-success, #00bf62)' }}>
            <div className="bo-summary-item-label">Success</div>
            <div className="bo-summary-item-value" style={{ fontSize: 14, fontWeight: 500 }}>{createSuccess}</div>
          </div>
        </div>
      )}

      {/* Create admin form */}
      {createOpen && (
        <div className="bo-table-card" style={{ marginBottom: 24 }}>
          <div className="bo-table-header">
            <span className="bo-table-title">New Admin</span>
          </div>
          <form onSubmit={handleCreateAdmin} style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)' }}>
                  Email <span style={{ color: 'var(--color-error)' }}>*</span>
                </label>
                <input
                  className="bo-filter-input"
                  type="email"
                  value={caEmail}
                  onChange={(e) => setCaEmail(e.target.value)}
                  placeholder="admin@company.com"
                  required
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)' }}>
                  Company <span style={{ color: 'var(--color-error)' }}>*</span>
                </label>
                <select
                  className="bo-filter-select crewcast-select"
                  value={caCompany}
                  onChange={(e) => setCaCompany(e.target.value)}
                  required
                >
                  <option value="" disabled>Select a company…</option>
                  <option value={NEW_COMPANY}>➕ New company…</option>
                  {companies.map((c) => (
                    <option key={c.companyId} value={c.companyId}>{c.displayName}</option>
                  ))}
                </select>
              </div>
            </div>

            {caCompany === NEW_COMPANY && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)' }}>
                  New company name <span style={{ color: 'var(--color-error)' }}>*</span>
                </label>
                <input
                  className="bo-filter-input"
                  type="text"
                  value={caNewCompanyName}
                  onChange={(e) => setCaNewCompanyName(e.target.value)}
                  placeholder='e.g. "Papa John&apos;s"'
                />
                <span style={{ fontSize: 12, color: 'var(--color-gray-500)' }}>
                  If a company with a matching slug already exists, it will be reused instead.
                </span>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)' }}>
                  First name
                </label>
                <input
                  className="bo-filter-input"
                  type="text"
                  value={caFirstName}
                  onChange={(e) => setCaFirstName(e.target.value)}
                  placeholder="Jane"
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)' }}>
                  Last name
                </label>
                <input
                  className="bo-filter-input"
                  type="text"
                  value={caLastName}
                  onChange={(e) => setCaLastName(e.target.value)}
                  placeholder="Doe"
                />
              </div>
            </div>

            <p style={{ fontSize: 12, color: 'var(--color-gray-500)', margin: 0 }}>
              The user receives an invitation email with a temporary password and is created with the <strong>admin</strong> role.
            </p>

            {createError && (
              <p style={{ color: 'var(--color-error)', fontSize: 13, margin: 0 }}>{createError}</p>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" className="bo-btn bo-btn-secondary" onClick={() => { setCreateOpen(false); resetCreateForm(); }}>
                Cancel
              </button>
              <button type="submit" className="bo-btn bo-btn-primary" disabled={creating}>
                {creating ? 'Creating…' : 'Send Invite'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Summary */}
      <div className="bo-summary-row">
        <div className="bo-summary-item">
          <div className="bo-summary-item-label">Companies</div>
          <div className="bo-summary-item-value">{loading ? '—' : total}</div>
        </div>
        <div className="bo-summary-item">
          <div className="bo-summary-item-label">Total Admins</div>
          <div className="bo-summary-item-value">{loading ? '—' : totalAdmins}</div>
        </div>
        <div className="bo-summary-item">
          <div className="bo-summary-item-label">Total Employees</div>
          <div className="bo-summary-item-value">{loading ? '—' : totalEmployees}</div>
        </div>
        <div className="bo-summary-item">
          <div className="bo-summary-item-label">Pending Invites</div>
          <div className="bo-summary-item-value">{loading ? '—' : totalPending}</div>
        </div>
      </div>

      {/* Search + actions */}
      <div className="bo-filters">
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8, flex: 1 }}>
          <div className="bo-filter-group" style={{ flex: 1, maxWidth: 360 }}>
            <label className="bo-filter-label">Search</label>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <Search size={14} style={{ position: 'absolute', left: 9, color: 'var(--color-gray-400)', pointerEvents: 'none' }} />
              <input
                className="bo-filter-input"
                style={{ paddingLeft: 30, width: '100%' }}
                type="text"
                placeholder="Company name or slug..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
          </div>
          <div className="bo-filter-group" style={{ justifyContent: 'flex-end' }}>
            <label className="bo-filter-label">&nbsp;</label>
            <button type="submit" className="bo-btn bo-btn-primary">Search</button>
          </div>
          <div className="bo-filter-group">
            <label className="bo-filter-label">Status</label>
            <select
              className="bo-filter-select crewcast-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
            >
              <option value="all">All</option>
              <option value="active">Not cancelled</option>
              <option value="canceled">Cancelled</option>
            </select>
          </div>
        </form>

        <div className="bo-filter-actions">
          <button className="bo-btn bo-btn-secondary" onClick={fetchData} disabled={loading}>
            <RefreshCw size={14} />
            Refresh
          </button>
          <button className="bo-btn bo-btn-secondary" onClick={handleExport} disabled={loading || companies.length === 0}>
            <Download size={14} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bo-table-card">
        <div className="bo-table-header">
          <span className="bo-table-title">
            Companies
            <span className="bo-table-count">({statusFilter === 'all' ? total : visibleCompanies.length})</span>
          </span>
        </div>

        {loading ? (
          <div className="bo-loading">Loading...</div>
        ) : error ? (
          <div className="bo-error">{error}</div>
        ) : visibleCompanies.length === 0 ? (
          <div className="bo-empty">No companies found.</div>
        ) : (
          <div className="bo-table-wrapper">
            <table className="bo-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Tier</th>
                  <th className="num">Admins</th>
                  <th className="num">Employees</th>
                  <th className="num">Total Users</th>
                  <th className="num">Logged In</th>
                  <th className="num">Pending</th>
                  <th>Last Activity</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {visibleCompanies.map((c) => (
                  <tr
                    key={c.companyId}
                    className="bo-row-clickable"
                    onClick={() => openDetail(c.companyId)}
                  >
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 'var(--font-weight-semibold)' as any }}>
                        {c.displayName}
                        {c.subscriptionStatus === 'canceled' && (
                          <span className="bo-badge bo-badge-red">Cancelled</span>
                        )}
                      </div>
                      <div style={{ fontSize: 'var(--font-size-xs)' as any, color: 'var(--color-gray-400)' as any }}>{c.slug}</div>
                    </td>
                    <td>
                      {c.subscriptionTier ? (
                        <span className="bo-badge bo-badge-blue">{c.subscriptionTier}</span>
                      ) : (
                        <span style={{ color: 'var(--color-gray-400)' }}>—</span>
                      )}
                    </td>
                    <td className="num">{c.adminCount}</td>
                    <td className="num">{c.employeeCount}</td>
                    <td className="num">{c.totalUsers}</td>
                    <td className="num">{c.loggedInCount}</td>
                    <td className="num">
                      {c.pendingCount > 0 ? (
                        <span className="bo-badge bo-badge-yellow">{c.pendingCount}</span>
                      ) : (
                        <span>0</span>
                      )}
                    </td>
                    <td>{c.lastActivityAt ? new Date(c.lastActivityAt).toLocaleDateString() : '—'}</td>
                    <td>{c.createdAt ? new Date(c.createdAt).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Company detail modal */}
      {(detailLoading || detail || detailError) && (
        <div className="bo-modal-overlay" onClick={() => closeDetail()}>
          <div className="bo-modal" style={{ maxWidth: 1280 }} onClick={(e) => e.stopPropagation()}>
            <div className="bo-modal-header">
              <div>
                <div className="bo-modal-title">
                  {detailLoading ? 'Loading…' : detail ? detail.company.displayName : 'Error'}
                </div>
                {detail && (
                  <div className="bo-modal-subtitle" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {detail.company.slug}
                    {isCanceled && <span className="bo-badge bo-badge-red">Cancelled</span>}
                  </div>
                )}
              </div>
              <button className="bo-modal-close" onClick={() => closeDetail()}>
                <X size={18} />
              </button>
            </div>

            <div className="bo-modal-body">
              {detailLoading && <div className="bo-loading">Loading company details…</div>}
              {detailError && <div className="bo-error">{detailError}</div>}

              {detail && (
                <>
                  {actionNotice && (
                    <div
                      className="bo-modal-section"
                      role="status"
                      style={{
                        padding: '10px 14px', borderRadius: 'var(--radius-md)', fontSize: 13,
                        background: 'var(--color-success-light)', color: 'var(--color-success-dark)',
                      }}
                    >
                      {actionNotice}
                    </div>
                  )}

                  {/* Account & billing (editable) */}
                  <div className="bo-modal-section">
                    <div className="bo-modal-section-title">Account &amp; Billing</div>
                    {accountError && (
                      <div className="bo-error" style={{ marginBottom: 8 }}>{accountError}</div>
                    )}
                    <div className="bo-detail-grid">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)' }}>Subscription Tier</label>
                        <select
                          className="bo-filter-select crewcast-select"
                          value={accountForm.subscriptionTier}
                          onChange={(e) => setAccountField('subscriptionTier', e.target.value)}
                        >
                          <option value="">—</option>
                          <option value="starter">starter</option>
                          <option value="growth">growth</option>
                          <option value="enterprise">enterprise</option>
                        </select>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)' }}>Subscription Status</label>
                        {/* Canceling revokes access, so it only happens via the
                            two-click Cancel/Reactivate actions in the Danger zone. */}
                        <select
                          className="bo-filter-select crewcast-select"
                          value={accountForm.subscriptionStatus}
                          onChange={(e) => setAccountField('subscriptionStatus', e.target.value)}
                          disabled={isCanceled}
                          title={isCanceled ? 'Use Reactivate company below to change this' : undefined}
                        >
                          <option value="">—</option>
                          <option value="trialing">trialing</option>
                          <option value="active">active</option>
                          <option value="past_due">past_due</option>
                          <option value="canceled" disabled={!isCanceled}>
                            {isCanceled ? 'canceled' : 'canceled (use Cancel company)'}
                          </option>
                        </select>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)' }}>Industry</label>
                        <input
                          className="bo-filter-input"
                          type="text"
                          value={accountForm.industry}
                          onChange={(e) => setAccountField('industry', e.target.value)}
                          placeholder="—"
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)' }}>Contact Email</label>
                        <input
                          className="bo-filter-input"
                          type="email"
                          value={accountForm.contactEmail}
                          onChange={(e) => setAccountField('contactEmail', e.target.value)}
                          placeholder="—"
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)' }}>Billing Email</label>
                        <input
                          className="bo-filter-input"
                          type="email"
                          value={accountForm.billingEmail}
                          onChange={(e) => setAccountField('billingEmail', e.target.value)}
                          placeholder="—"
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)' }}>Contract Start</label>
                        <input
                          className="bo-filter-input"
                          type="date"
                          value={accountForm.contractStartDate}
                          onChange={(e) => setAccountField('contractStartDate', e.target.value)}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)' }}>Contract End</label>
                        <input
                          className="bo-filter-input"
                          type="date"
                          value={accountForm.contractEndDate}
                          onChange={(e) => setAccountField('contractEndDate', e.target.value)}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)' }}>Billing Cycle</label>
                        <select
                          className="bo-filter-select crewcast-select"
                          value={accountForm.billingCycle}
                          onChange={(e) => setAccountField('billingCycle', e.target.value)}
                        >
                          <option value="">—</option>
                          <option value="monthly">Monthly</option>
                          <option value="bimonthly">Bi-monthly (every 2 months)</option>
                          <option value="yearly">Yearly</option>
                        </select>
                      </div>
                      <div>
                        <div className="bo-detail-item-label">Next Billing Date</div>
                        <div className="bo-detail-item-value">
                          {(() => {
                            const next = computeNextBillingDate(accountForm.contractStartDate, accountForm.billingCycle);
                            return next ? next.toLocaleDateString() : '—';
                          })()}
                        </div>
                      </div>
                      <div>
                        <div className="bo-detail-item-label">Created</div>
                        <div className="bo-detail-item-value">
                          {detail.company.createdAt ? new Date(detail.company.createdAt).toLocaleDateString() : '—'}
                        </div>
                      </div>
                      <div>
                        <div className="bo-detail-item-label">Last Activity</div>
                        <div className="bo-detail-item-value">
                          {detail.company.lastActivityAt ? new Date(detail.company.lastActivityAt).toLocaleDateString() : '—'}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
                      {accountSuccess && (
                        <span style={{ fontSize: 13, color: 'var(--color-success, #16a34a)' }}>{accountSuccess}</span>
                      )}
                      <button className="bo-btn bo-btn-primary" onClick={saveAccount} disabled={savingAccount}>
                        {savingAccount ? 'Saving…' : 'Save changes'}
                      </button>
                    </div>
                  </div>

                  {/* Company settings (editable) */}
                  <div className="bo-modal-section">
                    <div className="bo-modal-section-title">Settings</div>
                    {settingsError && (
                      <div className="bo-error" style={{ marginBottom: 8 }}>{settingsError}</div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {([
                        { key: 'suggestedTrendsEnabled', label: 'Suggested Trends widget', def: false },
                        { key: 'pointsEnabled', label: 'Points / rewards enabled', def: true },
                      ] as const).map(({ key, label, def }) => {
                        const checked = detail.company.settings?.[key] ?? def;
                        return (
                          <label
                            key={key}
                            style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: savingSetting ? 'default' : 'pointer' }}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={savingSetting !== null}
                              onChange={(e) => toggleCompanySetting(key, e.target.checked)}
                            />
                            <span className="bo-detail-item-value">{label}</span>
                            {savingSetting === key && (
                              <span className="bo-detail-item-label">Saving…</span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* User list (collapsible) */}
                  <CollapsibleSection
                    title={`Users (${detail.users.length})`}
                    open={usersOpen}
                    onToggle={() => setUsersOpen((v) => !v)}
                  >
                    {detail.users.length === 0 ? (
                      <div className="bo-empty" style={{ padding: '16px 0' }}>No users found.</div>
                    ) : (
                      <div className="bo-table-wrapper" style={{ border: '1px solid var(--color-gray-200)', borderRadius: 'var(--radius-md)' }}>
                        <table className="bo-table">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Email</th>
                              <th>Role</th>
                              <th>Status</th>
                              <th>Last Login</th>
                              <th>Invited</th>
                              <th aria-label="Actions" />
                            </tr>
                          </thead>
                          <tbody>
                            {detail.users.map((u) => {
                              const name = [u.firstName, u.lastName].filter(Boolean).join(' ') || '—';
                              const isActive = !!u.lastLoginAt;
                              const canAct = !isRemovedUser(u) && u.role !== 'superadmin' && u.userId !== me?.userId;
                              return (
                                <tr key={u.userId}>
                                  <td>{name}</td>
                                  <td>{u.status === 'deleted' ? '—' : u.email}</td>
                                  <td>
                                    <span className={`bo-badge ${u.role === 'admin' ? 'bo-badge-blue' : u.role === 'superadmin' ? 'bo-badge-red' : 'bo-badge-gray'}`}>
                                      {u.role}
                                    </span>
                                  </td>
                                  <td>
                                    {u.status === 'deleted' ? (
                                      <span className="bo-badge bo-badge-red">Deleted</span>
                                    ) : u.status === 'archived' ? (
                                      <span className="bo-badge bo-badge-gray">Archived</span>
                                    ) : u.status === 'suspended' ? (
                                      <span className="bo-badge bo-badge-red">Suspended</span>
                                    ) : isActive ? (
                                      <span className="bo-badge bo-badge-green">Active</span>
                                    ) : (
                                      <span className="bo-badge bo-badge-yellow">Invited</span>
                                    )}
                                  </td>
                                  <td>{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : '—'}</td>
                                  <td>{u.invitedAt ? new Date(u.invitedAt).toLocaleDateString() : '—'}</td>
                                  <td>
                                    {canAct && (
                                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                        <button
                                          type="button"
                                          className="bo-btn bo-btn-secondary bo-btn-sm"
                                          onClick={() => openAction({ kind: 'archiveUser', user: u })}
                                        >
                                          {u.status === 'invited' ? 'Remove invite' : 'Archive'}
                                        </button>
                                        {u.status !== 'invited' && (
                                          <button
                                            type="button"
                                            className="bo-btn bo-btn-danger-outline bo-btn-sm"
                                            onClick={() => openAction({ kind: 'deleteUser', user: u })}
                                          >
                                            Delete
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CollapsibleSection>

                  {/* Moments list (collapsible, lazy-loaded) */}
                  <CollapsibleSection
                    title={momentsLoadedFor === detailCompanyId ? `Moments (${moments.length})` : 'Moments'}
                    open={momentsOpen}
                    onToggle={toggleMoments}
                  >
                    {momentsLoading ? (
                      <div className="bo-loading">Loading moments…</div>
                    ) : momentsError ? (
                      <div className="bo-error">{momentsError}</div>
                    ) : moments.length === 0 ? (
                      <div className="bo-empty" style={{ padding: '16px 0' }}>No moments found.</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {moments.map((m) => (
                          <div
                            key={m.challengeId}
                            style={{
                              display: 'flex', gap: 12, padding: 12,
                              border: '1px solid var(--color-gray-200)', borderRadius: 'var(--radius-md)',
                            }}
                          >
                            {m.image ? (
                              <img
                                src={m.image}
                                alt=""
                                style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, flexShrink: 0, border: '1px solid var(--color-gray-200)' }}
                              />
                            ) : (
                              <div style={{ width: 56, height: 56, borderRadius: 6, flexShrink: 0, background: 'var(--color-gray-100)' }} />
                            )}
                            <div style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: 'var(--font-weight-semibold)' as any }}>{m.title}</span>
                                <span className={`bo-badge ${m.status === 'active' ? 'bo-badge-green' : m.status === 'complete' ? 'bo-badge-gray' : 'bo-badge-yellow'}`}>
                                  {m.status}
                                </span>
                                <span className="bo-badge bo-badge-blue">{m.points} pts</span>
                                {m.collaboratorsEnabled && (
                                  <span className="bo-badge bo-badge-gray">Collaborative</span>
                                )}
                              </div>
                              {m.description && (
                                <div style={{ fontSize: 'var(--font-size-sm)' as any, color: 'var(--color-gray-600)', marginTop: 4, overflowWrap: 'anywhere' }}>
                                  {m.description}
                                </div>
                              )}
                              {m.rules && (
                                <div style={{ fontSize: 'var(--font-size-xs)' as any, color: 'var(--color-gray-500)', marginTop: 4, overflowWrap: 'anywhere' }}>
                                  <strong>Rules:</strong> {m.rules}
                                </div>
                              )}
                              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 6, fontSize: 'var(--font-size-xs)' as any, color: 'var(--color-gray-500)' }}>
                                <span>{m.submissionsTotal} submissions</span>
                                <span style={{ color: 'var(--color-gray-700)' }}>{m.submissionsApproved} approved</span>
                                <span>{m.submissionsPending} pending</span>
                                <span>{m.submissionsRejected} rejected</span>
                                <span>Created {m.createdAt ? new Date(m.createdAt).toLocaleDateString() : '—'}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CollapsibleSection>

                  {/* Rewards list (collapsible, lazy-loaded) */}
                  <CollapsibleSection
                    title={rewardsLoadedFor === detailCompanyId ? `Rewards (${rewards.length})` : 'Rewards'}
                    open={rewardsOpen}
                    onToggle={toggleRewards}
                  >
                    {rewardsLoading ? (
                      <div className="bo-loading">Loading rewards…</div>
                    ) : rewardsError ? (
                      <div className="bo-error">{rewardsError}</div>
                    ) : rewards.length === 0 ? (
                      <div className="bo-empty" style={{ padding: '16px 0' }}>No rewards found.</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {rewards.map((r) => (
                          <div
                            key={r.rewardId}
                            style={{
                              display: 'flex', gap: 12, padding: 12,
                              border: '1px solid var(--color-gray-200)', borderRadius: 'var(--radius-md)',
                            }}
                          >
                            {r.image ? (
                              <img
                                src={r.image}
                                alt=""
                                style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, flexShrink: 0, border: '1px solid var(--color-gray-200)' }}
                              />
                            ) : (
                              <div style={{ width: 56, height: 56, borderRadius: 6, flexShrink: 0, background: 'var(--color-gray-100)' }} />
                            )}
                            <div style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: 'var(--font-weight-semibold)' as any }}>{r.title}</span>
                                <span className={`bo-badge ${r.isActive ? 'bo-badge-green' : 'bo-badge-gray'}`}>
                                  {r.isActive ? 'active' : 'inactive'}
                                </span>
                                <span className="bo-badge bo-badge-blue">{r.points} pts</span>
                              </div>
                              {r.description && (
                                <div style={{ fontSize: 'var(--font-size-sm)' as any, color: 'var(--color-gray-600)', marginTop: 4, overflowWrap: 'anywhere' }}>
                                  {r.description}
                                </div>
                              )}
                              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 6, fontSize: 'var(--font-size-xs)' as any, color: 'var(--color-gray-500)' }}>
                                <span>{r.claimsTotal} claims</span>
                                <span style={{ color: 'var(--color-gray-700)' }}>{r.claimsFulfilled} fulfilled</span>
                                <span>{r.claimsClaimed} pending</span>
                                <span>{r.claimsCancelled} cancelled</span>
                                <span>{r.pointsClaimed} pts claimed</span>
                                <span>Created {r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '—'}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CollapsibleSection>

                  {/* Danger zone: cancel / reactivate the company */}
                  <div
                    className="bo-modal-section"
                    style={{ border: '1px solid var(--color-error-light)', borderRadius: 'var(--radius-md)', padding: 16 }}
                  >
                    <div className="bo-modal-section-title" style={{ color: 'var(--color-error-dark)' }}>Danger zone</div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                      <div style={{ fontSize: 13, color: 'var(--color-gray-700)', flex: 1, minWidth: 220 }}>
                        {isCanceled ? (
                          <>
                            <strong>This company is cancelled</strong>
                            {detail.company.canceledAt && ` (since ${new Date(detail.company.canceledAt).toLocaleDateString()})`}.
                            {' '}Its users can't sign in. Reactivating restores access immediately.
                          </>
                        ) : (
                          <>
                            <strong>Cancel company</strong>: every user at this company loses access
                            immediately. Data is kept and the company can be reactivated later.
                          </>
                        )}
                      </div>
                      {isCanceled ? (
                        <button
                          type="button"
                          className="bo-btn bo-btn-primary"
                          onClick={() => openAction({ kind: 'reactivateCompany' })}
                        >
                          Reactivate company
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="bo-btn bo-btn-danger-outline"
                          onClick={() => openAction({ kind: 'cancelCompany' })}
                          disabled={detail.company.companyId === me?.companyId}
                          title={detail.company.companyId === me?.companyId ? "You can't cancel your own company" : undefined}
                        >
                          Cancel company
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Second click of every destructive action */}
      {pendingAction && detail && (() => {
        const company = detail.company.displayName;
        if (pendingAction.kind === 'archiveUser' || pendingAction.kind === 'deleteUser') {
          const u = pendingAction.user;
          const who = (
            <>
              <strong>{userLabel(u)}</strong> ({u.email}) at <strong>{company}</strong>
            </>
          );
          const lastAdminWarning = u.role === 'admin' && liveAdminCount <= 1 && (
            <p style={{ margin: '12px 0 0', color: 'var(--color-error-dark)' }}>
              This is the company's only active admin. The company will be left without one.
            </p>
          );

          if (pendingAction.kind === 'archiveUser' && u.status === 'invited') {
            return (
              <ConfirmActionModal
                title={`Remove ${userLabel(u)}'s invite?`}
                confirmLabel="Remove invite"
                danger
                loading={actionLoading}
                error={actionError}
                onConfirm={runPendingAction}
                onClose={closeAction}
              >
                <p style={{ margin: 0 }}>
                  {who} hasn't accepted their invite yet. The invite will be deleted so the email can be invited again.
                </p>
                {lastAdminWarning}
              </ConfirmActionModal>
            );
          }

          if (pendingAction.kind === 'archiveUser') {
            return (
              <ConfirmActionModal
                title={`Archive ${userLabel(u)}?`}
                confirmLabel="Archive user"
                danger
                loading={actionLoading}
                error={actionError}
                onConfirm={runPendingAction}
                onClose={closeAction}
              >
                <p style={{ margin: 0 }}>
                  {who} will be signed out immediately and won't be able to log in. Their submissions stay
                  in the Content Vault. This can be undone by re-inviting their email.
                </p>
                {lastAdminWarning}
              </ConfirmActionModal>
            );
          }

          return (
            <ConfirmActionModal
              title={`Delete ${userLabel(u)}?`}
              confirmLabel="Delete user"
              danger
              loading={actionLoading}
              error={actionError}
              onConfirm={runPendingAction}
              onClose={closeAction}
            >
              <p style={{ margin: 0 }}>
                This deletes the account for {who}, the same as if they deleted it themselves. Their
                submissions are kept, but the account can't be restored. The email can later be invited
                as a brand-new user.
              </p>
              <p style={{ margin: '12px 0 0' }}><strong>This cannot be undone.</strong></p>
              {lastAdminWarning}
            </ConfirmActionModal>
          );
        }

        const liveUsers = detail.users.filter((u) => !isRemovedUser(u)).length;
        if (pendingAction.kind === 'cancelCompany') {
          return (
            <ConfirmActionModal
              title={`Cancel ${company}?`}
              confirmLabel="Cancel company"
              danger
              loading={actionLoading}
              error={actionError}
              onConfirm={runPendingAction}
              onClose={closeAction}
            >
              <p style={{ margin: 0 }}>
                All <strong>{liveUsers}</strong> {liveUsers === 1 ? 'user' : 'users'} at <strong>{company}</strong> will
                lose access immediately. Signed-in users are signed out, and nobody can log in.
              </p>
              <p style={{ margin: '12px 0 0' }}>
                No data is deleted. You can reactivate the company at any time.
              </p>
            </ConfirmActionModal>
          );
        }

        return (
          <ConfirmActionModal
            title={`Reactivate ${company}?`}
            confirmLabel="Reactivate company"
            loading={actionLoading}
            error={actionError}
            onConfirm={runPendingAction}
            onClose={closeAction}
          >
            <p style={{ margin: 0 }}>
              Users at <strong>{company}</strong> will be able to sign in again right away.
            </p>
          </ConfirmActionModal>
        );
      })()}
    </>
  );
}
